import React from 'react';

import { Typography } from '@mui/material';
import { type Theme } from '@mui/material/styles';

import type { LensCard, LensResolvedPeriod } from '~/types/leLens';

import { type LensMarkPick, type LensSelection } from '../cardView';
import { ECHARTS_ONLY_CARD_TYPES, LENS_CHART_RENDERER } from '../lensRenderer';

import LensChartBody from './LensChartBody';
import { type LensChartHandle } from './LensEChart';
import LensRechartsBody from './LensRechartsBody';

export type LensCardChartProps = {
  card: LensCard;
  shaped: { rows: Array<Record<string, unknown>>; columns: Array<string> };
  comparison?: {
    shaped: { rows: Array<Record<string, unknown>>; columns: Array<string> };
    period?: LensResolvedPeriod;
  };
  plotHeight: number;
  selection?: LensSelection;
  onSelect: (pick: LensMarkPick) => void;
  theme: Theme;
  truncated: boolean;
  chartRef?: React.Ref<LensChartHandle>;
  onReorderCategories?: (order: Array<string>) => void;
  /** The card's rows before ranges and cross-filter selections narrow them,
   *  forwarded to LensChartBody for a stable series-to-colour assignment.
   *  See LensChartBody's own Props for the full rationale. Read only by the
   *  ECharts renderer; the Recharts fallback ignores it. */
  unfilteredShaped?: { rows: Array<Record<string, unknown>>; columns: Array<string> };
};

/**
 * The one place that decides which chart library draws a card. Both bodies take
 * these props and read the same model, so LENS_CHART_RENDERER is the whole
 * revert: no commit has to come back out.
 */
export default function LensCardChart(props: LensCardChartProps) {
  if (LENS_CHART_RENDERER === 'recharts') {
    if ((ECHARTS_ONLY_CARD_TYPES as ReadonlyArray<string>).includes(props.card.type)) {
      return (
        <Typography color="text.secondary" variant="body2">
          This card type needs the ECharts renderer.
        </Typography>
      );
    }
    // The fallback draws SVG through Recharts, which has no instance to ask
    // for a raster, so the picture export is simply absent on that path.
    const { chartRef: _chartRef, onReorderCategories: _onReorder, ...rechartsProps } = props;
    return <LensRechartsBody {...rechartsProps} />;
  }
  return <LensChartBody {...props} />;
}
